import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { TemplateLocaleInput } from 'modules/graphql';
import { useLocalesQueryContext } from 'modules/campaigns/lib/contexts/LocalesQueryContext';

import { selectLocaleImprovementsEnabled } from 'core/userContext';
import { useSelector } from 'react-redux';
import { SelectedMessageMedium, UnselectedMessageMedium } from '../common';
import { useTemplateSingleContentEditById } from '../hooks/useTemplateSingleContentEditById';
import { useQueryParamContext } from './QueryParamContext';
import { TemplateQueryContext, TemplateQueryContextState } from './TemplateQueryContext';

const useMedium = (messageMedium: SelectedMessageMedium) => {
  const [medium, setMedium] = useState<SelectedMessageMedium | null>(null);
  useEffect(() => {
    if (!medium && messageMedium !== UnselectedMessageMedium.Unselected) setMedium(messageMedium);
  }, [messageMedium]);

  return medium || UnselectedMessageMedium.Unselected;
};

export const TemplateQueryContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const isLocaleImprovementsEnabled = useSelector(selectLocaleImprovementsEnabled);
  const lastDataLocale = useRef<string>();
  const lastDataTemplateId = useRef<number>();
  const { templateId: id = 0, locale: localeTag } = useQueryParamContext();
  // values that depend upon projectLocales should be suspended until it resolves

  const {
    allProjectLocales: projectLocales,
    isLoading: isLoadingTemplateLocales,
    templateDefaultLocale,
  } = useLocalesQueryContext();
  const templateDefaultLocaleTag = templateDefaultLocale?.tag || '';
  const localeImprovementsDefaultLocale = templateDefaultLocaleTag;
  const projectDefaultLocale = projectLocales[0]?.tag || '';
  const projectHasLocales = !!projectLocales?.length;

  let isDefaultLocale: boolean;

  // Empty string for localeTag means we are looking at default locale regardless of FF
  if (localeTag === '') {
    isDefaultLocale = true;
  } else if (isLocaleImprovementsEnabled) {
    isDefaultLocale = localeTag === localeImprovementsDefaultLocale;
  } else {
    isDefaultLocale = localeTag === projectDefaultLocale;
  }

  const queryOptions = useMemo(() => {
    if (isLocaleImprovementsEnabled) {
      const localeInput: TemplateLocaleInput = {
        // Avoids sending back empty string as locale name when viewing default locale
        // When locale improvements FF is on, send back the template-specific default locale name
        name: isDefaultLocale ? localeImprovementsDefaultLocale : localeTag,
      };
      return {
        // If project has no locales, send undefined for locale value
        variables: { id, locale: projectHasLocales ? localeInput : undefined },
        skip: isLoadingTemplateLocales,
      };
    } else {
      // When locale improvements FF is off and we are viewing the default locale, send back undefined as the locale value
      // Can be removed once locale improvements is GA
      const localeInput: TemplateLocaleInput | undefined =
        localeTag?.length && !isDefaultLocale ? { name: localeTag } : undefined;
      return {
        variables: { id, locale: localeInput },
      };
    }
  }, [id, localeTag, localeImprovementsDefaultLocale, isLoadingTemplateLocales, isDefaultLocale]);

  // This needs to be triggered after an update
  const {
    errorMessage = '',
    hasError,
    isLoading,
    isAssociatedWithActiveExperiment,
    medium,
    messageTypeId,
    templateType,
    previousDataLocale,
    previousDataTemplateId,
    refetch,
    results: nonDefaultContent,
  } = useTemplateSingleContentEditById({
    ...queryOptions,
    fetchPolicy: 'no-cache',
  });

  const messageMedium = useMedium(medium);

  /**
   * This should be used instead of `refetchTemplate` as they actually do the same thing. If you don't care about the promise, you can ignore the return value.
   */
  const refetchTemplateAsync = () => refetch(queryOptions.variables);

  /**
   * @deprecated Use refetchTemplateAsync instead
   */
  const refetchTemplate = () => {
    refetch(queryOptions.variables);
  };

  // Can remove once we no longer rely on locale being undefined to signify default locale (locales project)
  const shouldUpdateLastDataLocale =
    previousDataLocale ||
    ((previousDataLocale?.toLocaleLowerCase() || '') !== localeTag.toLowerCase() &&
      !isDefaultLocale) ||
    (!previousDataLocale && isDefaultLocale);
  // compensate for intermediate 'undefined' values (only recognize changes)
  lastDataLocale.current = shouldUpdateLastDataLocale ? previousDataLocale : lastDataLocale.current;
  lastDataTemplateId.current = previousDataTemplateId ?? lastDataTemplateId.current;

  const context: TemplateQueryContextState = {
    errorMessage,
    hasError,
    isLoading: isLoading || isLoadingTemplateLocales,
    isAssociatedWithActiveExperiment,
    messageTypeId,
    templateType,
    medium: messageMedium,
    previousDataLocale: lastDataLocale.current,
    previousDataTemplateId: lastDataTemplateId.current,
    refetch: refetchTemplate,
    refetchTemplateAsync,
    template: nonDefaultContent,
  };

  return <TemplateQueryContext.Provider value={context}>{children}</TemplateQueryContext.Provider>;
};
